import React, { useMemo, useRef, useState } from "react"
import { Form, Row, Col, FormProps, Button, InputNumber, Input, Alert, notification, Checkbox, Switch, message, Radio } from "antd"
import StickyTable from "../../components/table/StickyTable"   // tog'a #4695/#4703: hamma jadvalda sarlavha va «Итого» qotadi
import { DeleteOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import WarehouseSelect from "../../components/form/WarehouseSelect"
import ResidueBatchPicker, { ResidueBatch } from "../../components/form/ResidueBatchPicker"   // tog'a #2420: tanlash OSTATKADAN
import useQueryParams from "../../hooks/useQueryParams"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { CREATE_TRANSFER_DOC } from "./mutations"
import { request } from "../../utils/config"   // S8-D: partiya qoldig‘i (Residues/BatchesLeft)
import { useScanTarget } from "../../components/scanner/useScanTarget"   // tog‘a #2065 W3
import { CREATE_DELIVERY } from "../deliveries/mutations"   // tog'a #3484: perevoddan yetkazma (sotuvdagidek)
import DriverSelect from "../../components/form/DriverSelect"
import NewDriverModal from "../../components/form/NewDriverModal"
import { permSync } from "../../utils/permSync"
import { useQuery } from "@tanstack/react-query"

export interface ISaleForm extends FormProps {}

interface ITransferItem {
  purchaseId: number
  productId?: number     // tog'a #3484: yetkazma tarkibi uchun
  name: string
  article: string
  barCode?: string       // talabnoma §5 (2026-09-06)
  document?: string      // partiya (manba zakupka hujjati)
  unit?: string
  available: number
  warehouseId: number
  warehouseName: string
  count: number
  note: string
  salePriceUSD?: number | null     // tog'a #2888: «Цена продажи» (serverdan, o'zgarmaydi)
  transferPriceUSD?: number | null // tog'a #2888: aniq «Цена перевода» (standart — sotuv narxi)
  wholesalePriceUSD?: number | null // tog'a #4741: optom narx (serverdan)
}

// 1-bosqich TZ: bitta perevod hujjatida KO'P tovar/partiya — dinamik jadval,
// qoldiq nazorati, jami hisob. Tovarlar PurchaseSelect (ko'p tanlash) orqali
// qo'shiladi; har qator uchun miqdor qoldiqdan oshmasligi bloklanadi.
export default function TransferForm({ ...props }: ISaleForm) {
  const { t } = useTranslation()
  const query = useQueryParams()
  const queryClient = useQueryClient()
  const [items, setItems] = useState<ITransferItem[]>([])

  // W3 (2026-09-16, tog‘a #2065): forma skan NISHONI — skanerlangan tovarning partiyasi savatga tushadi.
  // Partiya soni `Residues/Scan` dan keladi va u s254 dan beri PARTIYA DAFTARI bo‘yicha hisoblanadi,
  // shuning uchun bu yerda ko‘rinadigan son serverdagi chegara bilan bir xil.
  const scanRootRef = useRef<HTMLDivElement>(null)
  useScanTarget(scanRootRef, {
    kind: "transfer",
    needBatch: true,   // tog'a #2537: bir nechta partiya — tanlash (S8: aniq partiya)
    onScan: (hit) => {
      const p = hit.item.product
      const list = hit.item.batches || []
      const b = hit.batchNumber ? list.find((x) => x.batchNumber === hit.batchNumber) : list[0]
      if (!b || !b.purchaseId) return   // partiyasi yo‘q (qoldiq 0) — qo‘shilmaydi
      setTimeout(() => refreshAvailable([Number(b.purchaseId)]), 0)   // tog'a #2888: sotuv narxi ham shu yerdan
      setItems((prev) => {
        const ex = prev.find((x) => x.purchaseId === b.purchaseId)
        if (ex) {
          // S3: takror skan +1, lekin partiya qoldig‘idan oshmaydi
          const nc = ex.count + 1 > ex.available ? ex.available : ex.count + 1
          return prev.map((x) => (x.purchaseId === b.purchaseId ? { ...x, count: nc } : x))
        }
        const av = Number(b.amount) || 0
        return [
          ...prev,
          {
            purchaseId: b.purchaseId,
            productId: (p as any)?.id ?? (b as any)?.productId,
            name: p.name,
            article: p.article || "",
            barCode: p.barCode || "",
            document: b.batchNumber || b.document || "",
            unit: p.unitName || "",
            available: av,
            warehouseId: b.warehouseId || 0,
            warehouseName: b.warehouseName || "",
            count: av > 0 ? 1 : 0,
            note: "",
          },
        ]
      })
    },
  })
  const [toWarehouseId, setToWarehouseId] = useState<number | undefined>()
  const [docNote, setDocNote] = useState("")
  const [hideQty, setHideQty] = useState(false)   // tog'a #3248: «скрытый приём»
  // tog'a #4741 (2-band): perevod narxi turi — «dona» (sukut, 2.2) yoki «optom»; tanlansa hamma qatorlar shu narxdan to'ladi
  const [narxTuri, setNarxTuri] = useState<"dona" | "optom">("dona")
  const turNarxi = (x: ITransferItem, tur: "dona" | "optom") =>
    tur === "optom" && Number(x.wholesalePriceUSD || 0) > 0 ? Number(x.wholesalePriceUSD) : x.salePriceUSD ?? null
  const narxTuriniQoy = (tur: "dona" | "optom") => {
    setNarxTuri(tur)
    setItems((prev) => prev.map((x) => ({ ...x, transferPriceUSD: turNarxi(x, tur) })))
  }
  // tog'a #3484 (2026-10-04): perevod oynasida ham «🚚 Доставка» — sotuvdagidek: shofyor kartasidan, qabul qiluvchi — boradigan ombor
  const dlvRuxsat = permSync("deliveries").canCreate
  const [dlvOn, setDlvOn] = useState(false)
  const [dlv, setDlv] = useState<any>({})
  const dlvSet = (k: string, v: any) => setDlv((o: any) => ({ ...o, [k]: v }))
  const [dlvNewDriver, setDlvNewDriver] = useState(false)
  const [dlvDriverKey, setDlvDriverKey] = useState(0)
  const applyDlvDriver = (row: any) => {
    if (!row) return
    setDlv((o: any) => ({
      ...o,
      driverId: row.id,
      driverUserId: row.userId ?? null,
      driverName: [row.name, row.lastname].filter(Boolean).join(" "),
      driverPhone: row.phone || "",
      vehicleInfo: [row.vehicleInfo, row.vehiclePlate].filter(Boolean).join(" "),
      driverLicenseFile: row.licenseFile || null,
      vehiclePassportFile: row.vehiclePassportFile || null,
    }))
  }
  const toWh = useQuery(["TR_DLV_WH", toWarehouseId], async () => (await request({ url: `Warehouses/${toWarehouseId}` })).data, { enabled: !!toWarehouseId && dlvOn, staleTime: 60000 })
  const toWhRow: any = (toWh.data as any)?.data || toWh.data || {}

  const fromWarehouse = items.length > 0 ? items[0] : null

  const { mutate: createDoc, isLoading, error } = useMutation(CREATE_TRANSFER_DOC, {
    onSuccess: (res: any) => {
      notification.success({
        message: `Перевод ${res?.transferNumber || ""} создан`,
        description: `Позиций: ${res?.created ?? items.length}`,
      })
      // tog'a #3484: «Доставка» yoqilgan bo'lsa — perevod tovarlari bilan yetkazma (qabul qiluvchi — boradigan ombor)
      if (dlvOn && dlvRuxsat) {
        const tarkib = items.filter((x) => x.productId && Number(x.count) > 0).map((x) => ({ productId: Number(x.productId), quantity: Number(x.count) }))
        CREATE_DELIVERY({
          WarehouseId: fromWarehouse?.warehouseId || null,
          RecipientName: dlv.recipientName || toWhRow?.name || null,
          RecipientPhone: dlv.recipientPhone || null,
          RecipientAddress: dlv.recipientAddress || toWhRow?.address || null,
          DriverName: dlv.driverName || null,
          DriverPhone: dlv.driverPhone || null,
          VehicleInfo: dlv.vehicleInfo || null,
          DriverId: dlv.driverId || null,
          DriverUserId: dlv.driverUserId || null,
          DriverLicenseFile: dlv.driverLicenseFile || null,
          VehiclePassportFile: dlv.vehiclePassportFile || null,
          Comment: [`Перевод ${res?.transferNumber || ""}: ${fromWarehouse?.warehouseName || ""} → ${toWhRow?.name || ""}`, dlv.comment].filter(Boolean).join(" · "),
          Items: tarkib,
          Status: 0,
        } as any)
          .then((r: any) => { message.success("🚚 Доставка создана: " + (r?.deliveryNumber || r?.DeliveryNumber || "")); setDlv({}); setDlvOn(false) })
          .catch((e: any) => message.error("Доставка не создана: " + (e?.response?.data?.message || e?.message || "")))
      }
      setItems([])
      setDocNote("")
      queryClient.invalidateQueries()
      query.remove("transfer")
    },
  })

  // PurchaseSelect'dan tanlangan partiyalar — mavjud qatorlar saqlanadi,
  // yangilari qo'shiladi, olib tashlanganlari o'chadi
  // S8-D (2026-09-16): «mavjud» son PARTIYA DAFTARIDAN o‘qiladi — server ham aynan shu bo‘yicha to‘sadi.
  // `mainPurchase.Amount` ni faqat perevod kamaytiradi (sotuv, qaytarish va vitrina tegmaydi), shuning uchun u
  // allaqachon sotilgan tovarni ham «bor» deb ko‘rsatardi.
  async function refreshAvailable(ids: number[]) {
    if (!ids.length) return
    try {
      const r = await request({ url: "Residues/BatchesLeft", params: { ids: ids.join(",") } })
      const rows: any[] = (r as any)?.data?.items || []
      const map = new Map<number, number>(rows.map((x: any) => [Number(x.purchaseId), Number(x.amount) || 0]))
      const narx = new Map<number, number | null>(
        rows.map((x: any) => [Number(x.purchaseId), x.salePriceUSD == null ? null : Number(x.salePriceUSD)])
      )
      const optNarx = new Map<number, number | null>(
        rows.map((x: any) => [Number(x.purchaseId), x.wholesalePriceUSD == null ? null : Number(x.wholesalePriceUSD)])
      )   // tog'a #4741
      setItems((prev) =>
        prev.map((x) => {
          if (!map.has(x.purchaseId)) return x
          const av = map.get(x.purchaseId) as number
          const sp = narx.has(x.purchaseId) ? narx.get(x.purchaseId) : x.salePriceUSD
          const wp = optNarx.has(x.purchaseId) ? optNarx.get(x.purchaseId) : x.wholesalePriceUSD
          const y = { ...x, available: av, count: x.count > av ? av : x.count, salePriceUSD: sp, wholesalePriceUSD: wp }
          // tog'a #2888: aniq narx hali kiritilmagan bo'lsa — tanlangan narx turidan (#4741) to'ladi (keyin o'zgartiriladi)
          return { ...y, transferPriceUSD: x.transferPriceUSD == null ? turNarxi(y, narxTuri) : x.transferPriceUSD }
        })
      )
    } catch {
      // tarmoq xatosi — eski son qoladi; serverdagi chegara baribir to‘sadi
    }
  }

  // tog'a #2420 (2026-09-23): tanlash oynasi endi «Закупки» jurnali emas — QOLDIQDAN (Residues/AvailableBatches, partiya daftari).
  // Tanlanganlar mavjud ro'yxatga QO'SHILADI (oynani qayta ochib yana tovar qo'shish mumkin), takrorlari o'tkazib yuboriladi.
  function handleBatchesSelected(rows: ResidueBatch[]) {
    setItems((prev) => {
      const bor = new Set(prev.map((x) => x.purchaseId))
      const qoshilgan = (rows || [])
        .filter((b) => b && b.purchaseId && !bor.has(b.purchaseId))
        .map((b) => {
          const available = Number(b.left) || 0
          return {
            purchaseId: b.purchaseId,
            productId: b.productId,
            name: b.name || `Партия #${b.purchaseId}`,
            article: b.article || "",
            barCode: b.barCode || "",
            document: b.batchNumber || b.document || "",
            unit: b.unitName || "",
            available,
            warehouseId: b.warehouseId || 0,
            warehouseName: b.warehouseName || "",
            count: available > 0 ? Math.min(1, available) : 0,
            note: "",
          }
        })
      return [...prev, ...qoshilgan]
    })
    // qoldiq oyna ochilgandan beri o'zgargan bo'lishi mumkin — saqlashdan oldin yana bir bor daftardan so'raladi
    refreshAvailable((rows || []).map((b) => Number(b.purchaseId)).filter(Boolean))
  }

  function updateItem(purchaseId: number, patch: Partial<ITransferItem>) {
    setItems((prev) =>
      prev.map((x) => (x.purchaseId === purchaseId ? { ...x, ...patch } : x))
    )
  }

  function removeItem(purchaseId: number) {
    setItems((prev) => prev.filter((x) => x.purchaseId !== purchaseId))
  }

  const totals = useMemo(
    () => ({
      positions: items.length,
      quantity: items.reduce((s, x) => s + (Number(x.count) || 0), 0),
      // tog'a #2888: perevod summasi = Σ son × aniq perevod narxi
      sumUSD: items.reduce((s, x) => s + (Number(x.count) || 0) * (Number(x.transferPriceUSD) || 0), 0),
      // tog'a #4741: taqqoslash uchun ikkala jami
      donaUSD: items.reduce((s, x) => s + (Number(x.count) || 0) * (Number(x.salePriceUSD) || 0), 0),
      optomUSD: items.reduce((s, x) => s + (Number(x.count) || 0) * (Number(x.wholesalePriceUSD || x.salePriceUSD) || 0), 0),
    }),
    [items]
  )

  // Msg 153 tuzatishi: turli manba omborlarga RUXSAT — har qator o'z omboridan
  // jo'natiladi. Faqat "tovar allaqachon qabul qiluvchi omborda" holati bloklanadi.
  const distinctWh = useMemo(
    () => Array.from(new Set(items.map((x) => Number(x.warehouseId)))),
    [items]
  )
  const validationError = useMemo(() => {
    if (items.length === 0) return null
    if (toWarehouseId) {
      const already = items.find((x) => Number(x.warehouseId) === Number(toWarehouseId))
      if (already)
        return `«${already.name}» уже находится на складе-получателе — уберите его из списка или выберите другой склад`
    }
    const badPrice = items.find((x) => x.transferPriceUSD != null && (Number(x.transferPriceUSD) < 0 || Number(x.transferPriceUSD) > 1000000))
    if (badPrice) return `«${badPrice.name}»: ${t("trPriceRange")}`
    const bad = items.find((x) => !x.count || x.count <= 0 || x.count > x.available)
    if (bad)
      return `«${bad.name}»: количество должно быть от 0.01 до ${bad.available}`
    return null
  }, [items, toWarehouseId])

  const canSubmit =
    items.length > 0 && !!toWarehouseId && !validationError && !isLoading

  function handleSubmit() {
    if (!canSubmit || !toWarehouseId) return
    createDoc({
      toWarehouseId,
      note: docNote || undefined,
      hideQty: hideQty || undefined,
      priceKind: narxTuri,   // tog'a #4741: hujjatda saqlanadi
      items: items.map((x) => ({
        purchaseId: x.purchaseId,
        transferCount: x.count,
        note: x.note || undefined,
        transferPriceUSD: x.transferPriceUSD == null ? undefined : Number(x.transferPriceUSD),   // tog'a #2888
      })),
    })
  }

  const columns = [
    {
      title: "№",
      key: "idx",
      width: 45,
      render: (_: any, __: any, i: number) => i + 1,
    },
    {
      title: t("product"),
      key: "name",
      render: (_: any, r: ITransferItem) => (
        <span>{r.name}</span>
      ),
    },
    {
      title: t("barCode"),
      key: "barCode",
      width: 110,
      render: (_: any, r: ITransferItem) => <span style={{ color: "#64748b" }}>{r.barCode || "—"}</span>,
    },
    {
      title: t("article"),   // tog'a #2032: artikul alohida ustun
      key: "article",
      width: 110,
      render: (_: any, r: ITransferItem) => <span style={{ color: "#64748b" }}>{r.article || "—"}</span>,
    },
    {
      title: t("numberParty"),
      key: "document",
      width: 110,
      render: (_: any, r: ITransferItem) => <span style={{ color: "#64748b" }}>{r.document || "—"}</span>,
    },
    {
      title: t("warehouse"),
      key: "wh",
      width: 110,
      render: (_: any, r: ITransferItem) => (
        <span style={{ color: "#64748b" }}>{r.warehouseName || `#${r.warehouseId}`}</span>
      ),
    },
    {
      title: t("residue"),
      key: "available",
      width: 90,
      render: (_: any, r: ITransferItem) => (
        <b style={{ color: "#0369a1" }}>{r.available}</b>
      ),
    },
    {
      title: t("quantity"),
      key: "count",
      width: 150,   // tog'a #2262: son kiritish maydoni bemalol sig'sin
      render: (_: any, r: ITransferItem) => (
        <InputNumber
          min={0.01}
          max={r.available}
          step={1}
          value={r.count}
          status={r.count > r.available || r.count <= 0 ? "error" : undefined}
          onChange={(v) => updateItem(r.purchaseId, { count: Number(v) || 0 })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: t("posUnit"),   // «Ед.»
      key: "unit",
      width: 60,   // tog'a #2262: «Кол-во» surishsiz sig'sin
      render: (_: any, r: ITransferItem) => <span style={{ color: "#64748b" }}>{r.unit || t("gpPcs")}</span>,
    },
    {
      // tog'a #2888 (2026-09-26): tovarning sotuv narxi (avtomatik) va yonida aniq perevod narxi (qo'lda)
      title: t("trSalePrice"),
      key: "salePrice",
      width: 100,
      render: (_: any, r: ITransferItem) => (
        <span style={{ color: "#64748b" }}>{r.salePriceUSD == null ? "—" : `$${Number(r.salePriceUSD).toLocaleString("ru")}`}</span>
      ),
    },
    {
      // tog'a #4741: optom narx (qo'yilmagan — «—»)
      title: t("spWholesale", "Оптовая цена ($)"),
      key: "wholesalePrice",
      width: 100,
      render: (_: any, r: ITransferItem) => (
        <span style={{ color: "#874d00" }}>{r.wholesalePriceUSD == null || !(Number(r.wholesalePriceUSD) > 0) ? "—" : `$${Number(r.wholesalePriceUSD).toLocaleString("ru")}`}</span>
      ),
    },
    {
      title: t("trTransferPrice"),
      key: "transferPrice",
      width: 130,
      render: (_: any, r: ITransferItem) => (
        <InputNumber
          data-testid="tr-price"
          min={0}
          max={1000000}
          step={1}
          prefix="$"
          value={r.transferPriceUSD as any}
          onChange={(v) => updateItem(r.purchaseId, { transferPriceUSD: v == null ? null : Number(v) })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: t("note"),
      key: "note",
      width: 130,   // tog'a #2262
      render: (_: any, r: ITransferItem) => (
        <Input
          size="small"
          placeholder={t("batchNote")}
          value={r.note}
          onChange={(e) => updateItem(r.purchaseId, { note: e.target.value })}
        />
      ),
    },
    {
      title: "",
      key: "act",
      width: 40,
      render: (_: any, r: ITransferItem) => (
        <Button
          size="small"
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeItem(r.purchaseId)}
        />
      ),
    },
  ]

  return (
    // Msg 185: forma drawer chetiga yopishmasin — yon padding
    <div ref={scanRootRef}>   {/* W3: skan nishoni — hook ildiz elementini talab qiladi */}
    <Form {...props} layout="vertical" requiredMark onFinish={handleSubmit} style={{ padding: "4px 10px" }}>
      <Row gutter={16}>
        <Col xs={24}>
          <Form.Item label={t("product") + " (можно выбрать несколько)"} required>
            <ResidueBatchPicker onSelectRows={handleBatchesSelected} />
          </Form.Item>
        </Col>

        {items.length > 0 && (
          <Col xs={24}>
            {/* tog'a #4741 (2-band): perevod narxi turi — hamma qatorlar shu narxdan to'ladi (qatorda qo'lda o'zgartirish mumkin) */}
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
              <b>{t("trPriceKind", "Цена перевода:")}</b>
              <Radio.Group size="small" buttonStyle="solid" value={narxTuri} onChange={(e) => narxTuriniQoy(e.target.value)} data-testid="tr-price-kind">
                <Radio.Button value="dona">{t("priceKindUnit", "Поштучная цена")}</Radio.Button>
                <Radio.Button value="optom">{t("priceKindWholesale", "Оптовая цена")}</Radio.Button>
              </Radio.Group>
              <span style={{ color: "#94a3b8", fontSize: 12 }}>{t("trPriceKindHint", "Влияет на сумму перевода и «Складской учёт»; себестоимость, остатки и долги не меняются.")}</span>
            </div>
            <StickyTable
              rowKey="purchaseId"
              size="small"
              bordered
              pagination={false}
              columns={columns as any}
              dataSource={items}
              // tog'a #2262 (2026-09-21): `x: 600` da ustun kengliklari 600px ga SIQILARDI (table-layout: fixed
              // e'lon qilingan kengliklarni nisbatan kichraytiradi) — «Кол-во» maydoni faqat strelkalar sig'adigan
              // darajada torayib, son kiritish noqulay edi. `max-content`: kengliklar to'liq saqlanadi, jadval o'zi suriladi.
              scroll={{ x: "max-content" }}
              footer={() => (
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 16, fontWeight: 600, flexWrap: "wrap" }}>
                  <span>Позиций: {totals.positions}</span>
                  <span>Общее кол-во: {totals.quantity}</span>
                  <span style={{ color: "#64748b", fontWeight: 500 }}>{t("priceKindUnit", "Поштучная цена")}: ${Math.round(totals.donaUSD * 100) / 100}</span>
                  <span style={{ color: "#874d00", fontWeight: 500 }}>{t("priceKindWholesale", "Оптовая цена")}: ${Math.round(totals.optomUSD * 100) / 100}</span>
                  <span data-testid="tr-sum">{t("trSum")}: ${Math.round(totals.sumUSD * 100) / 100}</span>
                </div>
              )}
            />
          </Col>
        )}

        <Col sm={12} xs={24} style={{ marginTop: 12 }}>
          <Form.Item label={t("oldWarehouseName")} required>
            <Input
              size="large"
              disabled
              value={
                items.length === 0
                  ? t("productNotSelectedDash")
                  : distinctWh.length > 1
                  ? `Смешанные склады (${distinctWh.length})`
                  : fromWarehouse?.warehouseName || "—"
              }
            />
          </Form.Item>
        </Col>
        <Col sm={12} xs={24} style={{ marginTop: 12 }}>
          <Form.Item label={t("newWarehouseName")} required>
            <WarehouseSelect
              hasEmpty={false}
              onChange={(v: any) => setToWarehouseId(Number(v) || undefined)}
            />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item label={t("docNote")}>
            <Input.TextArea
              rows={2}
              value={docNote}
              onChange={(e) => setDocNote(e.target.value)}
              placeholder={t("noteOptional")}
            />
          </Form.Item>
        </Col>

        {/* tog'a #3248 (2026-10-03): qabul qiluvchi sonni ko'rmaydi — o'zi sanab «По факту» qabul qiladi */}
        <Col xs={24} style={{ marginBottom: 12 }}>
          <Checkbox checked={hideQty} onChange={(e) => setHideQty(e.target.checked)} data-testid="transfer-hide-qty">
            🙈 <b>{t("trHideQty", "Скрыть количество от получателя")}</b>
          </Checkbox>
          <div style={{ fontSize: 12, color: "#64748b", marginLeft: 24 }}>{t("trHideQtyHint", "Склад-получатель не увидит количество и примет только «По факту» — сам пересчитает. Недостача запишется автоматически.")}</div>
        </Col>

        {/* tog'a #3484: perevod bilan birga yetkazma (logistika) — sotuv formasidagi blokning o'zi */}
        {dlvRuxsat && (
          <Col xs={24} style={{ marginBottom: 12 }}>
            <div style={{ border: "1px solid rgba(34,197,94,.45)", background: "rgba(34,197,94,.06)", borderRadius: 10, padding: "10px 12px" }} data-testid="transfer-delivery">
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <b>🚚 {t("delivery", "Доставка")}</b>
                <Switch checked={dlvOn} onChange={setDlvOn} data-testid="transfer-delivery-on" />
                <span style={{ fontSize: 12, color: "#64748b" }}>{t("trDeliveryHint", "Создать доставку для этого перевода (необязательно) — получатель: склад назначения")}</span>
              </div>
              {dlvOn && (
                <Row gutter={10} style={{ marginTop: 10 }}>
                  <Col sm={8} xs={24}>
                    <div style={{ fontSize: 12, marginBottom: 2 }}>{t("recipient", "Получатель")}</div>
                    <Input value={dlv.recipientName ?? ""} onChange={(e) => dlvSet("recipientName", e.target.value)} placeholder={toWhRow?.name || t("newWarehouseName")} />
                  </Col>
                  <Col sm={8} xs={24}>
                    <div style={{ fontSize: 12, marginBottom: 2 }}>{t("phone", "Телефон")}</div>
                    <Input value={dlv.recipientPhone || ""} onChange={(e) => dlvSet("recipientPhone", e.target.value)} placeholder="+998 ..." />
                  </Col>
                  <Col sm={8} xs={24}>
                    <div style={{ fontSize: 12, marginBottom: 2 }}>{t("address", "Адрес")}</div>
                    <Input value={dlv.recipientAddress ?? ""} onChange={(e) => dlvSet("recipientAddress", e.target.value)} placeholder={toWhRow?.address || ""} />
                  </Col>
                  <Col sm={12} xs={24} style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 12, marginBottom: 2 }}>{t("driver", "Водитель")} <a onClick={() => setDlvNewDriver(true)}>+ {t("newDriver", "Новый водитель")}</a></div>
                    <DriverSelect key={dlvDriverKey} value={dlv.driverId} onChange={(v: any) => { if (!v) setDlv((o: any) => ({ ...o, driverId: null, driverUserId: null, driverName: "", driverPhone: "", driverLicenseFile: null, vehiclePassportFile: null })) }} onSelectRow={applyDlvDriver} />
                  </Col>
                  <Col sm={6} xs={24} style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 12, marginBottom: 2 }}>{t("driverPhone", "Телефон водителя")}</div>
                    <Input value={dlv.driverPhone || ""} onChange={(e) => dlvSet("driverPhone", e.target.value)} placeholder="+998 ..." />
                  </Col>
                  <Col sm={6} xs={24} style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 12, marginBottom: 2 }}>{t("vehicle", "Машина")}</div>
                    <Input value={dlv.vehicleInfo || ""} onChange={(e) => dlvSet("vehicleInfo", e.target.value)} placeholder="Cobalt 01A123BC" />
                  </Col>
                  <Col xs={24} style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 12, marginBottom: 2 }}>{t("comment", "Комментарий")}</div>
                    <Input value={dlv.comment || ""} onChange={(e) => dlvSet("comment", e.target.value)} />
                  </Col>
                </Row>
              )}
              <NewDriverModal visible={dlvNewDriver} onClose={() => setDlvNewDriver(false)} onCreated={(row: any) => { applyDlvDriver(row); setDlvDriverKey((k) => k + 1) }} />
            </div>
          </Col>
        )}

        {validationError && (
          <Col xs={24} style={{ marginBottom: 12 }}>
            <Alert type="error" showIcon message={validationError} />
          </Col>
        )}
      </Row>

      <Button
        loading={isLoading}
        danger={!!error}
        disabled={!canSubmit}
        htmlType="submit"
        size="large"
        block
        type="primary"
      >
        {t("save")}
      </Button>
    </Form>
    </div>
  )
}
